'use client';
import { useEffect, useState } from 'react';
import { RoomEvent } from 'livekit-client';
import { useRoomContext } from '@livekit/components-react';
const PRODUCT_CARDS_TOPIC = 'product_cards';
const CART_UPDATE_TOPIC = 'cart_update';
const CART_SYNC_TOPIC = 'cart_sync';
/** Common cart-count selectors used across Shopify themes. Updating
 * each one we find covers Dawn, Debut, Brooklyn, Impulse, Prestige, etc.
 * without needing per-theme adapters. */
const CART_COUNT_SELECTORS = [
    '[data-cart-count]',
    '[data-cart-item-count]',
    '[data-header-cart-count]',
    '.cart-count',
    '.cart-count-bubble',
    '.cart__count',
    '.icon-cart__count',
    '.header-cart-count',
    '.site-header__cart-count',
    '#CartCount',
    '#cart-icon-bubble span',
    'cart-icon-bubble .cart-count-bubble span',
];
function updateCartCountDom(itemCount) {
    if (typeof document === 'undefined')
        return;
    const text = itemCount > 0 ? String(itemCount) : '';
    document.querySelectorAll(CART_COUNT_SELECTORS.join(',')).forEach((el) => {
        // Only touch a span/leaf with numeric content. Some themes nest icons inside
        // .cart-count, and overwriting the wrapper's textContent kills the icon.
        if (el.children.length === 0) {
            el.textContent = text;
        }
        else {
            const numericChild = Array.from(el.querySelectorAll('span, *')).find((c) => c.children.length === 0);
            if (numericChild)
                numericChild.textContent = text;
        }
        // Toggle common "empty" classes so the bubble hides when count is 0.
        el.classList.toggle('cart-count-bubble--empty', itemCount === 0);
        el.classList.toggle('hidden', itemCount === 0);
    });
}
async function fetchBrowserCart() {
    try {
        const r = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!r.ok)
            return null;
        return (await r.json());
    }
    catch (_a) {
        return null;
    }
}
function notifyThemeOfCartChange(cart) {
    if (typeof document === 'undefined')
        return;
    // Update visible count immediately — themes that don't listen for any of
    // our events still get the right number on the icon.
    if (cart)
        updateCartCountDom(cart.item_count);
    // Dispatch every common event name. Cheap; cheap themes listen for one.
    const detail = cart !== null && cart !== void 0 ? cart : undefined;
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    document.dispatchEvent(new CustomEvent('cart:section-render-update', { detail }));
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    }
}
/**
 * Reconcile the merchant's browser session cart to match the bot's
 * Storefront cart. `desired` is the FULL desired state (every item the
 * agent thinks should be in the cart, with target quantity).
 *
 *   - items present in `desired` but missing from the browser cart → /cart/add.js
 *   - items in both with mismatched quantity → /cart/update.js (which SETS, not adds)
 *   - items in the browser cart but NOT in `desired` → LEFT ALONE
 *     (the user may have added them directly on the site)
 *
 * embed-popup.js runs in a Shadow DOM on the merchant's page (not a
 * cross-origin iframe), so same-origin access to /cart/add.js and
 * /cart/update.js works without OAuth.
 */
async function syncBrowserCartToState(desired) {
    var _a, _b;
    if (typeof window === 'undefined')
        return;
    const current = await fetchBrowserCart();
    const currentByVariant = new Map((_b = (_a = current === null || current === void 0 ? void 0 : current.items) === null || _a === void 0 ? void 0 : _a.map((i) => [String(i.variant_id), i.quantity])) !== null && _b !== void 0 ? _b : []);
    // What needs to be ADDED (in desired but not in current).
    const toAdd = desired.filter((d) => !currentByVariant.has(String(d.id)));
    // What needs its quantity SET (in both, qty differs).
    const updates = {};
    for (const d of desired) {
        const key = String(d.id);
        const haveQty = currentByVariant.get(key);
        if (haveQty !== undefined && haveQty !== d.quantity) {
            updates[key] = d.quantity;
        }
    }
    try {
        if (toAdd.length) {
            const r = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ items: toAdd }),
            });
            if (!r.ok) {
                const body = await r.text().catch(() => '');
                console.warn('[zalena] /cart/add.js failed:', r.status, body.slice(0, 200));
            }
        }
        if (Object.keys(updates).length) {
            const r = await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ updates }),
            });
            if (!r.ok) {
                const body = await r.text().catch(() => '');
                console.warn('[zalena] /cart/update.js failed:', r.status, body.slice(0, 200));
            }
        }
    }
    catch (err) {
        console.warn('[zalena] cart sync request failed:', err);
    }
    // Always tell the theme the cart changed, even if nothing was actually
    // sent (e.g., a get_cart that emitted cart_sync with a cart that already
    // matches the browser) — pages that have stale cart counts (the homepage
    // case) refresh on this signal.
    const updated = await fetchBrowserCart();
    notifyThemeOfCartChange(updated);
}
/**
 * Subscribe to the LiveKit data channel and turn Shopify-driven payloads
 * into local React state for the chat UI.
 *
 *   - `productEvents` — every search/details call produces one entry, in
 *     the order they happened. The transcript renders these alongside chat
 *     messages by interleaving on `timestamp`.
 *   - `cart` — the latest cart snapshot, or `null` when the cart is empty
 *     / not yet created. Used by the sticky `<CheckoutCta>` component.
 *
 * We DON'T forward the agent's text reply through this hook — that's still
 * the regular chat / transcript pipeline. Cards are a *side channel*, the
 * conversation stays on the main one.
 */
export function useShopifyEvents() {
    const room = useRoomContext();
    const [productEvents, setProductEvents] = useState([]);
    const [cart, setCart] = useState(null);
    useEffect(() => {
        if (!room)
            return;
        const handler = (payload, _participant, _kind, topic) => {
            if (topic !== PRODUCT_CARDS_TOPIC &&
                topic !== CART_UPDATE_TOPIC &&
                topic !== CART_SYNC_TOPIC)
                return;
            let parsed;
            try {
                parsed = JSON.parse(new TextDecoder().decode(payload));
            }
            catch (_a) {
                return;
            }
            if (!parsed || typeof parsed !== 'object')
                return;
            const data = parsed;
            if (topic === PRODUCT_CARDS_TOPIC && Array.isArray(data.products)) {
                const id = `pc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
                const event = {
                    id,
                    timestamp: Date.now(),
                    products: data.products.slice(0, 12), // safety cap
                };
                setProductEvents((prev) => [...prev, event]);
                return;
            }
            if (topic === CART_UPDATE_TOPIC) {
                if (data.cleared) {
                    setCart(null);
                    return;
                }
                setCart({
                    cleared: false,
                    item_count: typeof data.item_count === 'number' ? data.item_count : 0,
                    total_amount: typeof data.total_amount === 'number'
                        ? data.total_amount
                        : typeof data.total_amount === 'string'
                            ? Number(data.total_amount) || null
                            : null,
                    total_currency: typeof data.total_currency === 'string' ? data.total_currency : null,
                    checkout_url: typeof data.checkout_url === 'string' ? data.checkout_url : null,
                });
                return;
            }
            if (topic === CART_SYNC_TOPIC && Array.isArray(data.items)) {
                // Items shape from backend: [{id: number|string, quantity: number}].
                // This is the FULL desired browser-cart state, not a delta.
                const items = data.items
                    .map((it) => {
                    if (!it || typeof it !== 'object')
                        return null;
                    const o = it;
                    const rawId = o.id;
                    const id = typeof rawId === 'number'
                        ? rawId
                        : typeof rawId === 'string' && /^\d+$/.test(rawId)
                            ? Number(rawId)
                            : null;
                    const qty = typeof o.quantity === 'number' && o.quantity > 0 ? o.quantity : 1;
                    return id == null ? null : { id, quantity: qty };
                })
                    .filter((x) => x !== null);
                void syncBrowserCartToState(items);
                return;
            }
        };
        room.on(RoomEvent.DataReceived, handler);
        return () => {
            room.off(RoomEvent.DataReceived, handler);
        };
    }, [room]);
    return { productEvents, cart };
}
/**
 * Format an amount in minor currency units (e.g. 2999 = $29.99) for display.
 * Falls back to plain integer + currency code when Intl.NumberFormat can't
 * resolve the locale/currency.
 */
export function formatPrice(amount, currency) {
    if (amount == null || !currency)
        return null;
    const major = amount / 100;
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            maximumFractionDigits: 2,
        }).format(major);
    }
    catch (_a) {
        return `${major.toFixed(2)} ${currency}`;
    }
}
